/**
 * Build the orchestrator's `versionGating` deps from the live wiring of a
 * SqlMonaco editor. Reads the schema bus for `serverVersion` (all 5 engines
 * — the per-engine cache projectors populate it from the live connection)
 * and, for MSSQL, the projected `mssqlCompatLevel`, maps the renderer-side
 * engine id to the grammar's `SqlEngine`, and assembles a
 * `VersionGatingPostProcessHint`.
 *
 * Test seam: `window.__SQL_STUDIO_TEST_VERSION_GATING_OVERRIDE__` (gated on
 * `SQL_STUDIO_TEST=1` / `NODE_ENV='test'`) lets E2Es inject a synthetic
 * compat for mock-engine connections that don't have a real serverVersion.
 * The override is shallow: any field omitted falls back to the live value.
 */

import type { DatabaseEngineId } from '@/core/engines';
import type { CompatContext } from '@/features/intellisense/versionGating/resolveCompat';
import { databaseEngineIdToSqlEngine } from '@/features/intellisense/versionGating/engineIdMapping';
import { resolveCompat } from '@/features/intellisense/versionGating/resolveCompat';
import { getSnapshot as getSchemaBusSnapshot } from '@/features/intellisense/schemaBus/schemaBus';
import {
  resolveParseOptions,
  type SqlEngine,
} from '@/features/sql-grammar/parseOptions';

import type { VersionGatingPostProcessHint } from './versionGatingPostProcess';
import type { VersionFilterMode } from '@/features/intellisense/versionGating/decorate';

type TestOverride = {
  engineId?: SqlEngine;
  serverVersion?: string;
  mssqlCompatLevel?: number;
  mode?: VersionFilterMode;
};

function isTestModeEnabled(): boolean {
  if (typeof process === 'undefined') return false;
  if (process.env?.SQL_STUDIO_TEST === '1') return true;
  if (process.env?.NODE_ENV === 'test') return true;
  return false;
}

function readTestOverride(): TestOverride | undefined {
  if (!isTestModeEnabled()) return undefined;
  if (typeof window === 'undefined') return undefined;
  const w = window as unknown as { __SQL_STUDIO_TEST_VERSION_GATING_OVERRIDE__?: TestOverride };
  return w.__SQL_STUDIO_TEST_VERSION_GATING_OVERRIDE__;
}

export function computeVersionGatingHint(
  engineId: DatabaseEngineId,
  connectionId: string | undefined,
  database: string | undefined,
  documentText: string,
  defaultMode: VersionFilterMode,
): VersionGatingPostProcessHint | undefined {
  const override = readTestOverride();
  const sqlEngine = override?.engineId ?? databaseEngineIdToSqlEngine(engineId);
  if (!sqlEngine) return undefined;

  const busSnapshot = connectionId ? getSchemaBusSnapshot(connectionId, database) : null;
  const serverVersion = override?.serverVersion ?? busSnapshot?.serverVersion;
  // Production source is the projected bus snapshot (sourced from the live
  // connection's `sys.databases`); the test override still wins so E2Es can
  // inject a synthetic compat for mock-engine connections (INT-1).
  const mssqlCompatLevel = override?.mssqlCompatLevel ?? busSnapshot?.mssqlCompatLevel;

  const compat: CompatContext = resolveCompat({
    resolved: resolveParseOptions({
      engine: sqlEngine,
      documentText,
      serverVersion,
    }),
    mssqlCompatLevel,
  });

  const mode = override?.mode ?? defaultMode;
  return { compat, mode };
}
